import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, AfterViewInit, OnDestroy, ViewChildren, QueryList, ElementRef, ViewChild, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Html5Qrcode } from 'html5-qrcode';
import { CustomControlsService } from 'src/app/_service/custom-controls.service';
import { WalletinfoService } from 'src/app/_service/walletinfo.service';
import { MycontentserviceService } from 'src/app/_service/mycontentservice.service';
import { URLS } from 'src/app/constants/URLS';
import { environment } from 'src/environments/environment';
import { TVNavigationService, Region } from 'src/app/_service/tv-navigation.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';
import { IosBridgeService } from 'src/app/_service/ios-bridge.service';
import { MatTabGroup } from '@angular/material/tabs';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css']
})
export class SettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  isLoading:boolean=false;
  channelUrl:string = URLS.channelUrl;
  imgUrl:string = URLS.imgUrl
  activeSubscriptions:any[] = [];
  userOrders:any[] = [];
  transactions:any[] = [];
  watchHistory: any[] = [];
  isHistoryLoading: boolean = false;
  currentLang = localStorage.getItem('lang') || 'en';
  scanError = '';
  private html5QrCode!: Html5Qrcode;
  isScannerActive = false; 
  isScanProcessing = false; 
  languages = [
    { code: 'en', label: 'English', native: 'English', badge: 'Aa' },
    { code: 'hi', label: 'Hindi', native: 'हिंदी', badge: 'अ' },
    { code: 'te', label: 'Telugu', native: 'తెలుగు', badge: 'అ' },
    { code: 'id', label: 'Indonesian', native: 'Bahasa', badge: 'Ab' },
    { code: 'af', label: 'Afrikaans', native: 'Afrikaans', badge: 'Ab' },
    { code: 'zu', label: 'Zulu', native: 'isiZulu', badge: 'isi' },
    { code: 'de', label: 'German', native: 'Deutsch', badge: 'Äa' },
    { code: 'ru', label: 'Russian', native: 'Русский', badge: 'Я' }
  ];
  isMobile = false;
  isIOSApp = false;
  isRestoringPurchase = false;
  private nativeIAPHandler: any;

  // TV navigation properties
  @ViewChildren('langItem') langItems!: QueryList<ElementRef>;
  @ViewChild('languageContainer') languageContainer!: ElementRef;
  @ViewChild('tabGroup') tabGroup!: MatTabGroup;
  isTV = false;
  focusedItemIndex = -1;
  focusedTabIndex = -1;
  private focusSubscription?: Subscription;
  private regionId = 'settings-languages';
  private tabRegionId = 'settings-tabs';
  private tabElements: { nativeElement: HTMLElement }[] = [];

  constructor(
    private walletService: WalletinfoService,
    public translate: TranslateService,
    private http: HttpClient,
    private toastrService: CustomControlsService,
    private breakpointObserver: BreakpointObserver,
    private tvNav: TVNavigationService,
    private deviceService: DeviceService,
    private mycontentservice: MycontentserviceService,
    private ngZone: NgZone,
    private bridgeService: IosBridgeService
  ) { }

  ngOnInit(): void {
    this.isTV = this.deviceService.isTV();
    this.isIOSApp = this.bridgeService.isIOS();
    if (this.isIOSApp) {
      this.nativeIAPHandler = (e: CustomEvent) => {
        this.ngZone.run(() => {
          this.isRestoringPurchase = false;
          const eventType = e?.detail?.event;
          if (eventType === 'restoreCompleted') {
            this.showSuccess('All subscriptions restored');
            this.getUserSubscriptions();
          } else if (eventType === 'restoreFailed') {
            this.showWarning('Failed to restore subscriptions');
          }
        });
      };
      window.addEventListener('nativeIAPEvent', this.nativeIAPHandler);
    }
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe(result => {
      this.isMobile = result.matches;
    });
    this.isLoading = true;
    this.getUserSubscriptions();
  }

  ngAfterViewInit(): void {
    if (this.isTV) {
      setTimeout(() => {
        this.registerTabNavigation();
        this.registerSettingsNavigation();
        this.setupFocusSubscriptions();
      }, 200);

      // Re-register language region when items change (tab switched to Language)
      this.langItems.changes.subscribe(() => {
        this.registerSettingsNavigation();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.isTV) {
      this.tvNav.unregisterRegion(this.regionId);
      this.tvNav.unregisterRegion(this.tabRegionId);
      this.focusSubscription?.unsubscribe();
    }
    if (this.nativeIAPHandler) {
      window.removeEventListener('nativeIAPEvent', this.nativeIAPHandler);
    }
  }

  /** Register Material Tab headers as a navigable row region */
  private registerTabNavigation(): void {
    const tabLabels = document.querySelectorAll('.settings-tabs .mat-tab-label');
    if (!tabLabels.length) return;

    this.tabElements = Array.from(tabLabels).map(el => ({
      nativeElement: el as HTMLElement
    }));

    const tabRegion: Region = {
      id: this.tabRegionId,
      type: 'row',
      elementRef: this.tabElements[0] as any,
      focusableItems: this.tabElements as any[]
    };

    this.tvNav.registerRegion(tabRegion);
    // Start with focus on the currently selected tab
    this.tvNav.activateRegion(this.tabRegionId, this.tabGroup?.selectedIndex || 0);
  }

  /** Register language buttons as a navigable row region */
  private registerSettingsNavigation(): void {
    if (!this.langItems || this.langItems.length === 0) return;

    const region: Region = {
      id: this.regionId,
      type: 'row',
      elementRef: this.languageContainer || this.langItems.first,
      focusableItems: this.langItems.toArray()
    };

    this.tvNav.registerRegion(region);
  }

  /** Subscribe to focus changes — handle tab switching and content focus */
  private setupFocusSubscriptions(): void {
    this.focusSubscription?.unsubscribe();
    this.focusSubscription = this.tvNav.focus$.subscribe(focusKey => {
      if (!focusKey) return;

      const [regionId, indexStr] = focusKey.split(':');
      const index = parseInt(indexStr, 10);

      if (regionId === this.tabRegionId) {
        // Tab header focused — switch tab instantly
        this.focusedTabIndex = index;
        this.focusedItemIndex = -1;

        // Switch the active tab
        if (this.tabGroup && this.tabGroup.selectedIndex !== index) {
          this.ngZone.run(() => {
            this.tabGroup.selectedIndex = index;
          });
        }

        // Update tab header focus styling
        this.tabElements.forEach((el, i) => {
          if (i === index) {
            el.nativeElement.classList.add('tv-focus');
          } else {
            el.nativeElement.classList.remove('tv-focus');
          }
        });
      } else if (regionId === this.regionId) {
        // Language content focused
        this.focusedItemIndex = index;
        this.focusedTabIndex = -1;

        // Clear tab focus styling
        this.tabElements.forEach(el => el.nativeElement.classList.remove('tv-focus'));
      } else {
        this.focusedItemIndex = -1;
        this.focusedTabIndex = -1;
        this.tabElements.forEach(el => el.nativeElement.classList.remove('tv-focus'));
      }
    });
  }

  getUserSubscriptions(){
    this.walletService.getUserSubscriptions().subscribe((result) => {    
      this.userOrders = result;
      this.activeSubscriptions = result.filter(item => item.isActive);
      this.isLoading = false
    },(error)=>{
      this.isLoading = false;
    });
  }

  getUserTransactions(){
    this.walletService.getUserTransaction().subscribe((result) => {
      console.log(result);
      result.map((transaction)=>{
        if(transaction.eventType === "SUBSCRIPTION_FROM_WALLET" || transaction.eventType === "SUBSCRIPTION_FROM_CASHFREE"){
          this.transactions.push(transaction);
        }
      })
      console.log(this.transactions)
    });
  }

  onTabChange(event: any) {
    if (event.index === 3) {
      this.loadWatchHistory();
    }
  }

  loadWatchHistory() {
    if (this.watchHistory.length) return;
    this.isHistoryLoading = true;
    this.mycontentservice.getWatchHistory().subscribe(result => {
      this.watchHistory = result || [];
      this.isHistoryLoading = false;
    }, () => {
      this.isHistoryLoading = false;
    });
  }

  removeFromHistory(item: any) {
    this.mycontentservice.deleteWatchHistoryEntry(item.vidLibId).subscribe(() => {
      this.watchHistory = this.watchHistory.filter(m => m.vidLibId !== item.vidLibId);
      this.showSuccess('Removed from history');
    });
  }

  switchLang(lang: string) {
    if (lang === this.currentLang) return;
    this.currentLang = lang;
    this.translate.use(lang);
    localStorage.setItem('lang', lang);
    this.showSuccess('Language updated');
  }

  startQrScanner() {
    if (this.html5QrCode) return;
    console.log('Starting QR scanner');
    this.html5QrCode = new Html5Qrcode('qr-reader');
    this.html5QrCode.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 250 },
      (decodedText) => {
        try {
          const data = JSON.parse(decodedText);
          const requestId = data.request_id;
          this.isScanProcessing = true;
          this.stopQrScanner();
          this.scanQrRequest(requestId);
        } catch {
          this.scanError = 'Invalid QR code';
        }
      },
      () => {}
    ).catch(err => {
      console.error('QR start failed', err);
      this.scanError = 'Camera permission denied';
      this.isScannerActive = false;
    });
  }  
  
  stopQrScanner() {
    if (this.html5QrCode) {
      this.html5QrCode.stop().then(() => {
        this.html5QrCode.clear();
        this.html5QrCode = undefined as any;
      });
    }
  } 

  openScanner() {
    this.scanError = '';
    this.isScannerActive = true;
    requestAnimationFrame(() => {
      setTimeout(() => this.startQrScanner(), 300);
    });
  }

  closeScanner() {
    this.isScannerActive = false;
    this.isScanProcessing = false;
    this.stopQrScanner();
  }
  
  scanQrRequest(requestId: string) {
    this.isLoading = true;
    const formData = new FormData();
    formData.append('request_id', requestId);
    this.http.post(`${environment.TQR_SERVICE}masterSlaveController/approvePairRequest`,formData).subscribe({
      next: (result: any) => {
        this.isLoading = false;
        this.isScanProcessing = false;
        if (result.status === 'SUCCESS') {
          this.showSuccess('Device login approved');
          this.closeScanner(); 
        } else {
          this.showWarning('Approval failed');
        }
      },
      error: () => {
        this.isLoading = false;
        this.isScanProcessing = false;
        this.scanError = 'Failed to process QR request';
      }
    });
  }

  restorePurchase(): void {
    this.isRestoringPurchase = true;
    this.bridgeService.sendIosPaymentDetails({ action: 'restorePurchase' });
  }

  showSuccess(message: string): void {
    this.toastrService.showNotification('success', '', message, 3000);
  }

  showWarning(message: any) {
    this.toastrService.showNotification('warning', '', message, 5000);
  }
}